import { createRootRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { useEffect } from 'react';
import { AppShell } from '@/components/AppShell';
import { useAuthStore } from '@kapture/client-core';

/** Routes reachable without a session. */
const PUBLIC_ROUTES = ['/login', '/welcome', '/design-showcase'];

// Router devtools are opt-in now — set `VITE_ROUTER_DEVTOOLS=1` in
// `.env.local` to re-enable. Hidden by default so their floating
// button can't sit over the mobile FAB.
const ROUTER_DEVTOOLS_ENABLED =
  import.meta.env.DEV && import.meta.env['VITE_ROUTER_DEVTOOLS'] === '1';

/**
 * Module-augmented `staticData` shape. Routes declare which chrome mode
 * they want (mobile / fullscreen / modal) and AppShell reads this off
 * `useMatches()` to render the right chrome.
 */
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /** Use the compact 480px-wide mobile chrome. */
    mobile?: boolean;
    /** Hide AppShell chrome entirely — screen owns the viewport. */
    fullscreen?: boolean;
    /** Route renders as a sheet over a background location. */
    modal?: boolean;
  }
}

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

/**
 * Sends anonymous visitors to /login, remembering where they were headed.
 *
 * Deliberately not a `beforeLoad` guard: auth hydration is async and happens
 * during boot, so a loader-time check would bounce a returning user to /login
 * for the frame before their session is restored.
 */
function useAuthRedirect() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const status = useAuthStore((s) => s.status);

  useEffect(() => {
    if (status !== 'anonymous') return;
    if (PUBLIC_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`))) {
      return;
    }
    navigate({ to: '/login', search: { redirect: pathname }, replace: true });
  }, [status, pathname, navigate]);
}

function RootLayout() {
  useAuthRedirect();

  return (
    <>
      <AppShell>
        <Outlet />
      </AppShell>
      {ROUTER_DEVTOOLS_ENABLED && (
        <TanStackRouterDevtools position="top-left" initialIsOpen={false} />
      )}
    </>
  );
}

function NotFound() {
  return (
    <section className="mx-auto max-w-2xl px-6 py-20 text-center">
      <div className="text-link font-mono text-[11px] font-semibold uppercase tracking-wider">
        404
      </div>
      <h1 className="mt-3 font-sans text-3xl font-bold tracking-[-0.02em]">Not found</h1>
      <p className="text-text-dim mt-2 text-sm">
        That route doesn't exist. Try the{' '}
        <Link to="/" className="text-link underline-offset-2 hover:underline">
          home page
        </Link>
        .
      </p>
    </section>
  );
}
